import { isAxiosError } from 'axios';
import { ChipTone } from './index.style';
import { AmbienteEmissao, EmissorPainel, EstadoCertificado, SituacaoEmissor } from './types';

export const SITUACAO_LABEL: Record<SituacaoEmissor, { label: string; tone: ChipTone }> = {
  OPERACIONAL: { label: 'Operacional', tone: 'ok' },
  PENDENTE: { label: 'Pendente', tone: 'bad' },
  INATIVO: { label: 'Inativo', tone: 'mute' },
};

export const AMBIENTE_LABEL: Record<AmbienteEmissao, string> = {
  HOMOLOGACAO: 'Homologação',
  PRODUCAO: 'Produção',
};

export const certificadoChip = (
  estado: EstadoCertificado,
  diasParaVencer: number | null,
): { label: string; tone: ChipTone } => {
  switch (estado) {
    case 'VALIDO':
      return { label: `Válido · ${diasParaVencer ?? 0} dias`, tone: 'ok' };
    case 'VENCE_EM_BREVE':
      return {
        label: diasParaVencer === 0 ? 'Vence hoje' : `Vence em ${diasParaVencer} dias`,
        tone: 'warn',
      };
    case 'VENCIDO':
      return { label: 'Vencido', tone: 'bad' };
    case 'AUSENTE':
      return { label: 'Sem certificado', tone: 'bad' };
  }
};

export const formatDate = (isoDate: string | null): string => {
  if (!isoDate) return '—';
  const [y, m, d] = isoDate.slice(0, 10).split('-');
  return y && m && d ? `${d}/${m}/${y}` : isoDate;
};

export const formatCnpj = (cnpj: string | null): string => {
  const digits = (cnpj ?? '').replace(/\D/g, '');
  if (digits.length !== 14) return cnpj ?? '—';
  return digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
};

export const emissorTitulo = (emissor: EmissorPainel): string => {
  const nome = emissor.dados?.nomeFantasia || emissor.empresa.nome || emissor.dados?.razaoSocial || `Emissor ${emissor.id}`;
  const local = [emissor.empresa.cidade, emissor.empresa.uf].filter(Boolean).join(' ');
  return local ? `${nome} · ${local}` : nome;
};

export const extractApiError = (error: unknown, fallback: string): { message: string; campos: string[] } => {
  if (isAxiosError<{ message?: string | string[]; campos?: string[] }>(error)) {
    const msg = error.response?.data?.message;
    const campos = error.response?.data?.campos ?? [];
    if (Array.isArray(msg)) return { message: msg.join(', '), campos };
    if (typeof msg === 'string') return { message: msg, campos };
  }
  return { message: fallback, campos: [] };
};

export const certificadoUtilizavel = (estado: EstadoCertificado): boolean =>
  estado === 'VALIDO' || estado === 'VENCE_EM_BREVE';

export type AcaoPendencia = 'certificado' | 'reativar';

export interface Pendencia {
  tone: ChipTone;
  texto: string;
  acao: AcaoPendencia;
  rotulo: string;
}

export const pendenciaDoEmissor = (emissor: EmissorPainel): Pendencia | null => {
  const { estado, vencimento } = emissor.certificado;
  if (emissor.situacao === 'INATIVO')
    return {
      tone: 'mute',
      texto: 'Emissor inativo: não aparece para emitir notas novas. As notas já emitidas continuam consultáveis e canceláveis.',
      acao: 'reativar',
      rotulo: 'Reativar emissor',
    };
  if (estado === 'VENCIDO')
    return {
      tone: 'bad',
      texto: `Certificado vencido em ${formatDate(vencimento)}. Carregue um certificado válido para voltar a emitir.`,
      acao: 'certificado',
      rotulo: 'Carregar certificado',
    };
  if (estado === 'AUSENTE')
    return {
      tone: 'bad',
      texto: 'Emissor sem certificado digital. Carregue o certificado A1 para começar a emitir.',
      acao: 'certificado',
      rotulo: 'Carregar certificado',
    };
  if (estado === 'VENCE_EM_BREVE')
    return {
      tone: 'warn',
      texto: `O certificado vence em ${formatDate(vencimento)}. Carregue o novo antes para não parar a emissão.`,
      acao: 'certificado',
      rotulo: 'Substituir certificado',
    };
  return null;
};

export const CAMPO_EMPRESA_LABEL: Record<string, string> = {
  cnpj: 'CNPJ',
  inscricaoEstadual: 'inscrição estadual',
  razaoSocial: 'razão social',
  endereco: 'endereço',
  'endereco.uf': 'UF do endereço',
};
